// All AI tool endpoints. These currently hit Vercel serverless functions
// at /api/tools/*. If/when these move to the Joon backend (app.joonapp.io),
// only this file changes — components keep calling these functions unchanged.

export type GeneratedGoal = {
  title: string
  goal: string
  masteryCriteria: string
}

export type AbaGeneratedGoal = {
  goalStatement?: string
  objective?: string
  measurement?: string
}

export type TaskAnalysisStep = {
  stepNumber: number
  description: string
  difficultyLevel: string
  tips: string
}

export type TaskAnalysisResponse = {
  taskName: string
  materials: string[]
  prerequisites: string[]
  steps: TaskAnalysisStep[]
}

export type DataCollectionRecommendation = {
  method: string
  description: string
  suitability: string
}

async function postTool<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const text = await res.text()
  let data: any
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(
      `Invalid JSON response from ${path}: ${text.substring(0, 200)}`
    )
  }
  if (!res.ok) {
    throw new Error(data?.message || `Request failed: ${res.status}`)
  }
  return data as T
}

export const generateBcbaGoals = (topic: string) =>
  postTool<{ goals: GeneratedGoal[] }>("/api/tools/generateGoals", { topic })

export const generateAbaGoals = (clientInfo: string, goalType: string) =>
  postTool<{ goals: AbaGeneratedGoal[] }>("/api/tools/generateABAGoals", {
    clientInfo,
    goalType,
  })

export const generateTaskAnalysis = (task: string, context: string) =>
  postTool<TaskAnalysisResponse>("/api/tools/generateTaskAnalysis", {
    task,
    context,
  })

export const generateInterventionStrategies = (input: {
  targetBehavior: string
  clientCharacteristics: string
  environmentalFactors: string
  previousInterventions: string
}) =>
  postTool<{ strategies: string }>(
    "/api/tools/generateInterventionStrategies",
    input
  )

export const generateBipTemplate = (input: {
  targetBehaviors: string
  functionOfBehavior: string
  clientInfo: string
  currentInterventions: string
  environmentalFactors: string
}) =>
  postTool<{ bipTemplate: string }>("/api/tools/generateBIPTemplate", input)

export const generateProgressNoteTemplate = (
  description: string,
  goal: string
) =>
  postTool<{ template: string }>("/api/tools/generateProgressNoteTemplate", {
    description,
    goal,
  })

export const generateSessionSummary = (input: {
  rawNotes: string
  noteType: string
  templateStyle: string
}) =>
  postTool<{ summary: string }>("/api/tools/generateSessionSummary", input)

export const generateAbaAssessmentTemplate = (
  clientInfo: string,
  assessmentType: string
) =>
  postTool<{ template: string }>(
    "/api/tools/generateABAAssessmentTemplate",
    { clientInfo, assessmentType }
  )

export const generateDataCollectionRecommendations = (problem: string) =>
  postTool<{ recommendations: DataCollectionRecommendation[] }>(
    "/api/tools/generateDataCollectionRecommendations",
    { problem }
  )
